import type { AddressResult } from "@dransay/address-validation";
import { useCallback, useRef, useState } from "react";

interface ReverseGeocodeResponse {
	result?: AddressResult | null;
	error?: string;
}

export interface UseCurrentLocationReturn {
	requestLocation: () => void;
	isLocating: boolean;
	error: string | null;
}

export function useCurrentLocation(
	onResolved: (address: AddressResult) => void,
): UseCurrentLocationReturn {
	const [isLocating, setIsLocating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	const requestLocation = useCallback(() => {
		if (!navigator.geolocation) {
			setError("Geolocation is not supported by this browser");
			return;
		}

		setIsLocating(true);
		setError(null);

		navigator.geolocation.getCurrentPosition(
			async (position) => {
				const { latitude, longitude } = position.coords;

				abortControllerRef.current?.abort();
				const controller = new AbortController();
				abortControllerRef.current = controller;

				try {
					const params = new URLSearchParams({
						lat: latitude.toString(),
						lng: longitude.toString(),
					});

					const response = await fetch(
						`/api/address/reverse-geocode?${params}`,
						{
							signal: controller.signal,
							credentials: "same-origin",
						},
					);

					if (!response.ok) {
						setError("Failed to resolve your location to an address");
						return;
					}

					const data: ReverseGeocodeResponse = await response.json();

					if (data.error || !data.result) {
						setError("Could not find an address for your location");
						return;
					}

					onResolved(data.result);
				} catch (fetchError) {
					if (fetchError instanceof Error && fetchError.name === "AbortError") {
						return;
					}
					setError("Failed to resolve your location to an address");
				} finally {
					setIsLocating(false);
					abortControllerRef.current = null;
				}
			},
			(geoError) => {
				setIsLocating(false);
				switch (geoError.code) {
					case geoError.PERMISSION_DENIED:
						setError("Location permission denied");
						break;
					case geoError.POSITION_UNAVAILABLE:
						setError("Location unavailable");
						break;
					case geoError.TIMEOUT:
						setError("Location request timed out");
						break;
					default:
						setError("Failed to get your location");
				}
			},
			{ enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
		);
	}, [onResolved]);

	return { requestLocation, isLocating, error };
}
